<script setup lang="ts">
import { ActionButton, EmptyState, ErrorState, NavIcon } from '@taler/ui'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
  statisticsPeriodOf,
  canViewGarage,
  CarDetailSkeleton,
  CarForm,
  CarHeader,
  CarPhotoEditor,
  CarRecentEvents,
  CarSelector,
  CarStatisticsCard,
  EventForm,
  EventList,
  PolicyForm,
  PolicyList,
  ScheduleForm,
  ScheduleList,
  useCar,
  useCars,
  useCarPhotos,
  type CreateCarInput,
  type UpdateCarEventInput,
  type UpdateCarInput,
} from '@/features/garage'
import { localDateParts, systemClock, todayUtc } from '@taler/domain'
import { useDisplayCurrency } from '@/app/display-currency'
import { useSession } from '@/app/session'
import { useToasts } from '@/app/toasts'

const props = defineProps<{ carId?: string }>()

const { t, locale } = useI18n()
const router = useRouter()
const session = useSession()
const toasts = useToasts()
const { displayCurrency } = useDisplayCurrency()

const canView = computed(() => canViewGarage(session.can))
const showCreateForm = ref(false)

const cars = useCars({
  ledgerId: () => session.activeLedger.value?.id,
  enabled: () => canView.value,
})
const photoUrls = useCarPhotos({
  ledgerId: () => session.activeLedger.value?.id,
  cars: () => cars.cars.value,
})

const today = todayUtc(systemClock)
const currentYear = localDateParts(today).year
const statisticsYear = ref(currentYear)
const statisticsPeriod = computed(() => statisticsPeriodOf(statisticsYear.value, today))

const pinnedId = ref<string | undefined>(undefined)
const selectedId = computed(
  () =>
    props.carId ??
    pinnedId.value ??
    cars.cars.value.find((candidate) => candidate.isPrimary)?.id ??
    cars.cars.value[0]?.id,
)

const car = useCar({
  ledgerId: () => session.activeLedger.value?.id,
  carId: () => selectedId.value ?? '',
  enabled: () => canView.value && selectedId.value !== undefined,
  displayCurrency: () => displayCurrency.value,
  period: () => statisticsPeriod.value,
  locale: () => locale.value as 'en' | 'hu',
})

const createErrorMessage = computed(() =>
  cars.hasCreateError.value ? (cars.createErrorDetail.value ?? t('garage.form.createErrorBody')) : undefined,
)

// CarForm's submit event is shared with edit mode; the create form only ever emits a `CreateCarInput`.
function createCar(input: CreateCarInput | UpdateCarInput): void {
  cars.create(input as CreateCarInput).catch(() => {
    // surfaced through cars.hasCreateError; nothing further to do here
  })
}

watch(
  () => cars.isCreating.value,
  (isCreating, wasCreating) => {
    if (wasCreating && !isCreating && !cars.hasCreateError.value) showCreateForm.value = false
  },
)

watch(
  () => session.activeLedger.value?.id,
  () => {
    pinnedId.value = undefined
  },
)

function setPrimary(carId: string): void {
  pinnedId.value = selectedId.value
  cars.setPrimary(carId).catch(() => {
    toasts.push('danger', t('garage.setPrimaryError'))
  })
}

const TABS = ['overview', 'events', 'policies', 'schedules'] as const
type Tab = (typeof TABS)[number]
const tab = ref<Tab>('overview')
const editing = ref(false)
const loggingEvent = ref(false)
const sellingCar = ref(false)
const editingEventId = ref<string | undefined>(undefined)

// One page instance serves every car, so a switch resets the workspace the way a fresh load would.
watch(selectedId, () => {
  tab.value = 'overview'
  editing.value = false
  loggingEvent.value = false
  sellingCar.value = false
  editingEventId.value = undefined
})

const editingEvent = computed(() => car.events.value.find((event) => event.id === editingEventId.value))

const accountNames = computed(() => {
  const names = new Map<string, string>()
  for (const account of [
    ...car.expenseAccounts.value,
    ...car.sourceAccounts.value,
    ...car.incomeAccounts.value,
  ])
    names.set(account.id, account.label)
  return names
})

const updateErrorMessage = computed(() =>
  car.hasUpdateCarError.value
    ? (car.updateCarErrorDetail.value ?? t('garage.form.updateErrorBody'))
    : undefined,
)
// CarForm's submit event is shared with create mode; the edit form only ever emits an `UpdateCarInput`.
function saveCar(input: CreateCarInput | UpdateCarInput): void {
  car
    .updateCar(input as UpdateCarInput)
    .then(() => {
      editing.value = false
    })
    .catch(() => {
      // surfaced through car.hasUpdateCarError
    })
}

const photoErrorMessage = computed(() => {
  if (!car.hasReplacePhotoError.value) return undefined
  if (car.replacePhotoErrorCode.value === 'garage.photo_size_out_of_range')
    return t('garage.photo.errors.size')
  if (car.replacePhotoErrorCode.value === 'garage.photo_content_type_unsupported') {
    return t('garage.photo.errors.type')
  }
  return t('garage.photo.errors.generic')
})
function uploadPhoto(file: File): void {
  car.replacePhoto(file).catch(() => {
    // surfaced through car.hasReplacePhotoError
  })
}
function removePhoto(): void {
  car.deletePhoto().catch(() => {
    toasts.push('danger', t('garage.photo.removeError'))
  })
}

function deleteCar(): void {
  car
    .deleteCar()
    .then(() => {
      pinnedId.value = undefined
      return router.push({ name: 'garage' })
    })
    .catch(() => {
      toasts.push('danger', t('garage.deleteCarError'))
    })
}

const recordErrorMessage = computed(() =>
  car.hasRecordEventError.value
    ? (car.recordEventErrorDetail.value ?? t('garage.events.recordErrorBody'))
    : undefined,
)
function recordEvent(input: Parameters<typeof car.recordEvent>[0]): void {
  car
    .recordEvent(input)
    .then(() => {
      loggingEvent.value = false
      sellingCar.value = false
    })
    .catch(() => {
      // surfaced through car.hasRecordEventError
    })
}
function deleteEvent(eventId: string): void {
  car.deleteEvent(eventId).catch(() => {
    // best-effort
  })
}

const updateEventErrorMessage = computed(() =>
  car.hasUpdateEventError.value
    ? (car.updateEventErrorDetail.value ?? t('garage.events.recordErrorBody'))
    : undefined,
)
function editEvent(eventId: string): void {
  tab.value = 'events'
  editingEventId.value = eventId
}
function logEvent(): void {
  tab.value = 'events'
  loggingEvent.value = true
}
function updateEvent(eventId: string, input: UpdateCarEventInput): void {
  car
    .updateEvent(eventId, input)
    .then(() => {
      editingEventId.value = undefined
    })
    .catch(() => {
      // surfaced through car.hasUpdateEventError
    })
}

const createPolicyErrorMessage = computed(() =>
  car.hasCreatePolicyError.value
    ? (car.createPolicyErrorDetail.value ?? t('garage.policies.createErrorBody'))
    : undefined,
)
function createPolicy(input: Parameters<typeof car.createPolicy>[0]): void {
  car.createPolicy(input).catch(() => {
    // surfaced through car.hasCreatePolicyError
  })
}
function deletePolicy(policyId: string): void {
  car.deletePolicy(policyId).catch(() => {
    // best-effort
  })
}

const createScheduleErrorMessage = computed(() =>
  car.hasCreateScheduleError.value
    ? (car.createScheduleErrorDetail.value ?? t('garage.schedules.createErrorBody'))
    : undefined,
)
function createSchedule(input: Parameters<typeof car.createSchedule>[0]): void {
  car.createSchedule(input).catch(() => {
    // surfaced through car.hasCreateScheduleError
  })
}
function deleteSchedule(scheduleId: string): void {
  car.deleteSchedule(scheduleId).catch(() => {
    // best-effort
  })
}
function markScheduleDone(scheduleId: string, input: Parameters<typeof car.markScheduleDone>[1]): void {
  car.markScheduleDone(scheduleId, input).catch(() => {
    // best-effort
  })
}

const TAB_LABEL: Record<Tab, string> = {
  overview: 'garage.tabOverview',
  events: 'garage.events.title',
  policies: 'garage.policies.title',
  schedules: 'garage.schedules.title',
}
</script>

<template>
  <main class="mx-auto flex w-full max-w-[92.5rem] flex-col gap-6 p-[var(--space-page)]">
    <div class="grid gap-6 lg:grid-cols-[18rem_minmax(0,1fr)] xl:grid-cols-[20rem_minmax(0,1fr)]">
      <aside class="flex min-w-0 flex-col gap-4">
        <div class="flex items-center justify-between gap-3">
          <h1 class="text-[length:var(--text-step-6)] font-semibold text-[color:var(--color-text)]">
            {{ t('garage.title') }}
          </h1>
          <ActionButton
            v-if="canView && !showCreateForm"
            class="whitespace-nowrap"
            type="button"
            @click="showCreateForm = true"
          >
            {{ t('garage.addCar') }}
          </ActionButton>
        </div>
        <p class="text-[color:var(--color-text-muted)]">{{ t('garage.subtitle') }}</p>

        <CarSelector
          v-if="cars.cars.value.length > 0"
          :cars="cars.cars.value"
          :photo-urls="photoUrls"
          :current-id="selectedId"
          :can-manage="canView"
          @set-primary="setPrimary"
        />
      </aside>

      <div class="flex min-w-0 flex-col gap-4">
        <CarForm
          v-if="canView && showCreateForm"
          mode="create"
          :is-submitting="cars.isCreating.value"
          :error-message="createErrorMessage"
          class="rounded-[var(--radius-surface)] border border-[color:var(--color-border)] p-4 bg-[color:var(--color-surface)]"
          @submit="createCar"
          @cancel="showCreateForm = false"
        />

        <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>

        <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
          {{ t('garage.noLedger') }}
        </p>

        <p v-else-if="!canView" class="text-[color:var(--color-text-muted)]">
          {{ t('garage.noAccess') }}
        </p>

        <ErrorState
          v-else-if="cars.error.value"
          :title="t('garage.errorTitle')"
          :body="t('garage.errorBody')"
          :retry-label="t('garage.retry')"
          :retrying="cars.retrying.value"
          @retry="cars.retry()"
        />

        <EmptyState
          v-else-if="!cars.isLoading.value && cars.cars.value.length === 0"
          :title="t('garage.emptyTitle')"
          :body="t('garage.emptyBody')"
        >
          <template #icon><NavIcon name="garage" /></template>
        </EmptyState>

        <CarDetailSkeleton v-else-if="cars.isLoading.value || car.isLoading.value" />

        <ErrorState
          v-else-if="car.error.value"
          :title="t('garage.detailErrorTitle')"
          :body="t('garage.errorBody')"
          :retry-label="t('garage.retry')"
          :retrying="car.retrying.value"
          @retry="car.retry()"
        />

        <template v-else-if="car.car.value">
          <CarHeader
            :car="car.car.value"
            :photo-url="photoUrls.get(car.car.value.id)"
            :can-manage="canView"
            :is-deleting="car.isDeletingCar.value"
            @edit="editing = !editing"
            @delete="deleteCar"
          />

          <CarPhotoEditor
            v-if="editing"
            :has-photo="car.car.value.hasPhoto"
            :is-uploading="car.isReplacingPhoto.value"
            :is-removing="car.isDeletingPhoto.value"
            :is-busy="car.isPhotoRequestPending.value"
            :error-message="photoErrorMessage"
            @upload="uploadPhoto"
            @remove="removePhoto"
          />

          <CarForm
            v-if="editing"
            mode="edit"
            :initial="car.car.value"
            :is-submitting="car.isUpdatingCar.value"
            :error-message="updateErrorMessage"
            class="rounded-[var(--radius-surface)] border border-[color:var(--color-border)] p-4 bg-[color:var(--color-surface)]"
            @submit="saveCar"
            @cancel="editing = false"
          />

          <nav
            :aria-label="t('garage.tabsLabel')"
            class="flex gap-1 overflow-x-auto border-b border-[color:var(--color-border)]"
          >
            <button
              v-for="candidate in TABS"
              :key="candidate"
              type="button"
              class="whitespace-nowrap border-b-2 px-3 py-2 text-[length:var(--text-step-3)]"
              :class="
                tab === candidate
                  ? 'border-[color:var(--color-accent)] text-[color:var(--color-text)]'
                  : 'border-transparent text-[color:var(--color-text-muted)]'
              "
              @click="tab = candidate"
            >
              {{ t(TAB_LABEL[candidate]) }}
            </button>
          </nav>

          <section v-if="tab === 'overview'" class="flex flex-col gap-4">
            <CarStatisticsCard
              :statistics="car.statistics.value"
              :period="statisticsPeriod"
              :events="car.events.value"
              :account-names="accountNames"
              :year="statisticsYear"
              :current-year="currentYear"
              :loading="car.isLoadingStatistics.value"
              :failed="car.statisticsError.value !== null"
              @update:year="statisticsYear = $event"
              :retrying="car.statisticsRetrying.value"
              @retry="car.retryStatistics()"
            />
            <CarRecentEvents
              :events="car.events.value"
              :account-names="accountNames"
              :can-manage="canView"
              :can-log="canView && car.car.value.status === 'ACTIVE'"
              @log="logEvent"
              @edit="editEvent"
              @delete="deleteEvent"
            />
          </section>

          <section v-else-if="tab === 'events'" class="flex flex-col gap-4">
            <div v-if="canView" class="flex gap-2">
              <ActionButton
                v-if="!loggingEvent && !sellingCar && !editingEvent && car.car.value.status === 'ACTIVE'"
                type="button"
                @click="loggingEvent = true"
              >
                {{ t('garage.events.logEvent') }}
              </ActionButton>
              <button
                v-if="!loggingEvent && !sellingCar && !editingEvent && car.car.value.status === 'ACTIVE'"
                type="button"
                class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border)] px-3 py-2 text-[color:var(--color-text)]"
                @click="sellingCar = true"
              >
                {{ t('garage.events.sellCar') }}
              </button>
            </div>

            <p
              v-if="canView && car.car.value.status === 'SOLD' && !loggingEvent"
              class="text-[color:var(--color-text-muted)]"
            >
              {{ t('garage.events.alreadySold') }}
            </p>

            <EventForm
              v-if="loggingEvent"
              :car="car.car.value"
              :ledger-id="session.activeLedger.value?.id"
              :policies="car.policies.value"
              :schedules="car.schedules.value"
              :expense-accounts="car.expenseAccounts.value"
              :source-accounts="car.sourceAccounts.value"
              :income-accounts="car.incomeAccounts.value"
              :recent-entries="car.recentEntries.value"
              :is-submitting="car.isRecordingEvent.value"
              :error-message="recordErrorMessage"
              class="rounded-[var(--radius-surface)] border border-[color:var(--color-border)] p-4 bg-[color:var(--color-surface)]"
              @submit="recordEvent"
              @cancel="loggingEvent = false"
            />
            <EventForm
              v-else-if="sellingCar"
              initial-type="SELL"
              :car="car.car.value"
              :ledger-id="session.activeLedger.value?.id"
              :policies="car.policies.value"
              :schedules="car.schedules.value"
              :expense-accounts="car.expenseAccounts.value"
              :source-accounts="car.sourceAccounts.value"
              :income-accounts="car.incomeAccounts.value"
              :recent-entries="car.recentEntries.value"
              :is-submitting="car.isRecordingEvent.value"
              :error-message="recordErrorMessage"
              class="rounded-[var(--radius-surface)] border border-[color:var(--color-border)] p-4 bg-[color:var(--color-surface)]"
              @submit="recordEvent"
              @cancel="sellingCar = false"
            />
            <EventForm
              v-else-if="editingEvent"
              :editing-event="editingEvent"
              :car="car.car.value"
              :ledger-id="session.activeLedger.value?.id"
              :policies="car.policies.value"
              :schedules="car.schedules.value"
              :expense-accounts="car.expenseAccounts.value"
              :source-accounts="car.sourceAccounts.value"
              :income-accounts="car.incomeAccounts.value"
              :recent-entries="car.recentEntries.value"
              :is-submitting="car.isUpdatingEvent.value"
              :error-message="updateEventErrorMessage"
              class="rounded-[var(--radius-surface)] border border-[color:var(--color-border)] p-4 bg-[color:var(--color-surface)]"
              @update="updateEvent"
              @cancel="editingEventId = undefined"
            />

            <EventList
              v-if="!loggingEvent && !sellingCar && !editingEvent"
              :events="car.events.value"
              @edit="editEvent"
              @delete="deleteEvent"
            />
          </section>

          <section v-else-if="tab === 'policies'" class="flex flex-col gap-4">
            <PolicyForm
              v-if="canView"
              :is-submitting="car.isCreatingPolicy.value"
              :error-message="createPolicyErrorMessage"
              @create="createPolicy"
            />
            <PolicyList :policies="car.policies.value" @delete="deletePolicy" />
          </section>

          <section v-else-if="tab === 'schedules'" class="flex flex-col gap-4">
            <ScheduleForm
              v-if="canView"
              :is-submitting="car.isCreatingSchedule.value"
              :error-message="createScheduleErrorMessage"
              @create="createSchedule"
            />
            <ScheduleList
              :schedules="car.schedules.value"
              @delete="deleteSchedule"
              @mark-done="markScheduleDone"
            />
          </section>
        </template>
      </div>
    </div>
  </main>
</template>
